// pages/training/TrainingPdfViewer.tsx
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/joy';
import { ArrowSquareOut } from '@phosphor-icons/react';
import '../../lib/pdfWorkerSetup'; // Initialize PDF worker
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

interface TrainingPdfViewerProps {
  /** The item's media URL. */
  fileUrl: string;
}

/**
 * PDF training material, rendered with the app's react-pdf setup.
 *
 * Unlike the contract viewers this fetches nothing itself: training files are
 * media URLs, not authenticated API endpoints, so react-pdf loads them
 * directly. Pages are laid out at the container's width — on a phone that is
 * the screen, with no pinch-to-read.
 */
export const TrainingPdfViewer: React.FC<TrainingPdfViewerProps> = ({ fileUrl }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const measure = () => setWidth(containerRef.current?.clientWidth ?? 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  return (
    <Box ref={containerRef}>
      {error ? (
        <Alert color="danger" sx={{ mb: 1.5 }}>
          {error}
        </Alert>
      ) : (
        <Box sx={{ bgcolor: 'background.level2', borderRadius: 'sm', p: { xs: 0.5, md: 1 } }}>
          <Document
            file={fileUrl}
            onLoadSuccess={({ numPages: pages }) => {
              setNumPages(pages);
              setError(null);
            }}
            onLoadError={(loadError: Error) =>
              setError(`This PDF could not be displayed (${loadError.message}). Use the link below to open it.`)
            }
            loading={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            }
          >
            {Array.from({ length: numPages }, (_, index) => (
              <Box key={`page_${index + 1}`} sx={{ mb: 1 }}>
                <Page
                  pageNumber={index + 1}
                  width={width > 0 ? width - 16 : undefined}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Box>
            ))}
          </Document>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 1 }}>
        {numPages > 0 && !error ? (
          <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
            {numPages} page{numPages === 1 ? '' : 's'} — scroll to read it all
          </Typography>
        ) : (
          <span />
        )}
        <Button
          component="a"
          href={fileUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="sm"
          endDecorator={<ArrowSquareOut />}
        >
          Open PDF
        </Button>
      </Box>
    </Box>
  );
};

export default TrainingPdfViewer;
